/**
 * Public Booking API Service
 * Handles API calls for the self-service booking widget
 */

import type { UnifiedBookingStatus } from '@leal-crm/shared';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.lealchapaypintura.com';

interface ServiceType {
  id: string;
  name: string;
  nameEn: string | null;
  description: string | null;
  estimatedDurationMins: number;
}

interface TimeSlot {
  date: string;
  time: string;
  available: boolean;
  serviceTypes: Array<{
    id: string;
    availableCount: number;
  }>;
}

interface BookingRequest {
  serviceTypeId: string;
  scheduledAt: number;
  caseCategory?: 'particular' | 'seguro' | 'garantia';
  insurance?: {
    company: string;
    policyNumber?: string;
    claimNumber?: string;
    adjusterName?: string;
    adjusterPhone?: string;
    adjusterEmail?: string;
  };
  customer: {
    name: string;
    email: string;
    phone: string;
  };
  vehicle: {
    registration: string;
    make: string;
    model: string;
    year?: number;
  };
  notes?: string;
  gdprConsent: boolean;
  marketingConsent?: boolean;
  source?: 'public_widget' | 'marketing_link';
  campaignId?: string;
}

interface BookingConfirmation {
  bookingId: string;
  trackingToken: string;
  confirmationNumber?: string;
  scheduledAt: number;
  confirmationEmailSent: boolean;
}

interface SlotUnavailableError {
  code: 'SLOT_UNAVAILABLE';
  message: string;
  nextAvailable: TimeSlot[];
}

interface BookingStatus {
  bookingId: string;
  status: string;
  statusInfo?: {
    code: string;
    name: string;
    color: string;
    bgColor: string | null;
    borderColor: string | null;
    icon: string | null;
    statusType: string;
  } | null;
  scheduledAt: number;
  serviceType: string;
  vehicle: {
    registration: string;
    make: string;
    model: string;
  };
}

/**
 * Fetch active service types for the booking widget
 */
export async function getServiceTypes(): Promise<ServiceType[]> {
  const response = await fetch(`${API_BASE}/api/public/booking/service-types`);
  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error?.message || 'Failed to fetch service types');
  }

  return data.data;
}

/**
 * Fetch available time slots for a date range
 */
export async function getAvailability(
  startDate: string,
  endDate: string,
  serviceTypeId?: string
): Promise<TimeSlot[]> {
  const params = new URLSearchParams({ startDate, endDate });
  if (serviceTypeId) {
    params.append('serviceTypeId', serviceTypeId);
  }

  const response = await fetch(`${API_BASE}/api/public/booking/availability?${params}`);
  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error?.message || 'Failed to fetch availability');
  }

  return data.data.slots;
}

/**
 * Create a new booking
 */
export async function createBooking(
  booking: BookingRequest
): Promise<{ success: true; data: BookingConfirmation } | { success: false; error: SlotUnavailableError | { message: string } }> {
  const response = await fetch(`${API_BASE}/api/public/booking/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(booking),
  });

  const data = await response.json();
  return data;
}

/**
 * Get booking status by tracking token
 */
export async function getBookingStatus(trackingToken: string): Promise<BookingStatus | null> {
  const response = await fetch(`${API_BASE}/api/public/booking/status/${trackingToken}`);
  const data = await response.json();

  if (!data.success) {
    return null;
  }

  return data.data;
}

/**
 * Request magic link for customer lookup
 */
export async function requestMagicLink(email: string): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/public/booking/customer-lookup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error?.message || 'Request failed');
  }

  return { message: data.message };
}

interface MagicLinkVerifyResult {
  success: boolean;
  customerToken?: string;
  trackingToken?: string;
  error?: string;
}

/**
 * Verify magic link token and get session
 */
export async function verifyMagicLink(token: string): Promise<MagicLinkVerifyResult> {
  try {
    const response = await fetch(`${API_BASE}/api/portal/auth/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    const data = await response.json();

    if (!data.success) {
      return {
        success: false,
        error: data.error?.message || 'Enlace invalido o expirado',
      };
    }

    return {
      success: true,
      customerToken: data.data.sessionId,
      trackingToken: data.data.trackingToken,
    };
  } catch {
    return {
      success: false,
      error: 'Error al verificar el enlace',
    };
  }
}

/**
 * Get customer data using session token
 */
export async function getCustomerData(
  sessionId: string
): Promise<{
  customer: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  vehicles: Array<{
    id: string;
    registration: string;
    make: string;
    model: string;
    year: number | null;
  }>;
} | null> {
  const response = await fetch(`${API_BASE}/api/public/booking/customer-data`, {
    headers: {
      'X-Customer-Session': sessionId,
    },
  });

  const data = await response.json();

  if (!data.success) {
    return null;
  }

  return data.data;
}

/**
 * Format date for display
 */
export function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format time for display
 */
export function formatTime(time: string): string {
  return time;
}

/**
 * Get the workflow stage display name
 */
export function getStatusDisplayName(status: string, statusInfo?: { name?: string | null }): string {
  return statusInfo?.name || status;
}

/**
 * New tracking types for plate+reference lookup
 * Updated for unified booking status system
 */
export interface TrackingBookingInfo {
  id: string;
  bookingReference: string;
  status: UnifiedBookingStatus;
  workflowStage?: string; // Legacy, for backwards compat
  statusInfo?: {
    code: string;
    name: string;
    color: string;
    bgColor: string | null;
    borderColor: string | null;
    icon: string | null;
    statusType: string;
  } | null;
  scheduledAt: string;
  confirmed: boolean;
  serviceType?: {
    id: string;
    name: string;
  };
  service?: {
    id: string;
    name: string;
  };
  vehicle: {
    plate: string;
    make?: string;
    model?: string;
  };
  statusHistory: Array<{
    id: string;
    fromStage: string | null;
    toStage: string;
    publicNote: string | null;
    createdAt: string;
    toStatusInfo?: {
      code: string;
      name: string;
      color: string;
      bgColor: string | null;
      borderColor: string | null;
      icon: string | null;
      statusType: string;
    } | null;
  }>;
  timeline?: Array<{
    id: string;
    eventType: string;
    previousStatus: string | null;
    newStatus: string | null;
    publicNote: string | null;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface TrackingLookupResult {
  found: boolean;
  booking?: TrackingBookingInfo;
  error?: string;
}

/**
 * Look up booking by license plate and booking reference
 * This is the new primary lookup method for customers
 */
export async function lookupByPlateAndReference(
  plate: string,
  reference: string
): Promise<TrackingLookupResult> {
  try {
    const response = await fetch(`${API_BASE}/api/public/tracking/lookup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ plate, reference }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        found: false,
        error: data.error || 'Booking not found',
      };
    }

    return {
      found: true,
      booking: data.booking,
    };
  } catch (error) {
    return {
      found: false,
      error: 'Error connecting to server',
    };
  }
}

/**
 * Get booking status by reference with plate verification
 */
export async function getBookingStatusByReference(
  reference: string,
  plate: string
): Promise<TrackingLookupResult> {
  try {
    const params = new URLSearchParams({ plate });
    const response = await fetch(
      `${API_BASE}/api/public/tracking/status/${encodeURIComponent(reference)}?${params}`
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        found: false,
        error: data.error || 'Booking not found',
      };
    }

    return {
      found: true,
      booking: data.booking,
    };
  } catch (error) {
    return {
      found: false,
      error: 'Error connecting to server',
    };
  }
}

/**
 * Quick validation of plate + reference combination
 */
export async function validateTrackingLookup(
  plate: string,
  reference: string
): Promise<boolean> {
  try {
    const params = new URLSearchParams({ plate, reference });
    const response = await fetch(`${API_BASE}/api/public/tracking/validate?${params}`);
    const data = await response.json();
    return data.valid === true;
  } catch {
    return false;
  }
}

// ============================================================================
// Image Upload API
// ============================================================================

export interface BookingImageInfo {
  id: string;
  bookingId: string;
  url: string;
  contentType: string;
  fileSize: number;
  originalFilename?: string;
  uploadedAt: number;
}

export interface ImageUploadResult {
  success: boolean;
  imageId?: string;
  previewUrl?: string;
  error?: string;
}

/**
 * Upload an image for a booking
 */
export async function uploadBookingImage(
  trackingToken: string,
  file: File
): Promise<ImageUploadResult> {
  try {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE}/api/public/booking/upload-image`, {
      method: 'POST',
      headers: {
        'X-Tracking-Token': trackingToken,
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Upload failed',
      };
    }

    return {
      success: true,
      imageId: data.data.imageId,
      previewUrl: data.data.previewUrl,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
    };
  }
}

/**
 * Delete a booking image
 */
export async function deleteBookingImage(
  trackingToken: string,
  imageId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${API_BASE}/api/public/booking/delete-image/${imageId}`, {
      method: 'DELETE',
      headers: {
        'X-Tracking-Token': trackingToken,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Delete failed',
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Delete failed',
    };
  }
}

/**
 * Get all images for a booking
 */
export async function getBookingImages(
  bookingId: string,
  options: { trackingToken?: string; plate?: string; ref?: string }
): Promise<BookingImageInfo[]> {
  const headers: Record<string, string> = {};
  const params = new URLSearchParams();

  if (options.trackingToken) {
    headers['X-Tracking-Token'] = options.trackingToken;
  }
  if (options.plate) {
    params.append('plate', options.plate);
  }
  if (options.ref) {
    params.append('ref', options.ref);
  }

  const queryString = params.toString();
  const url = `${API_BASE}/api/public/booking/${bookingId}/images${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, { headers });
  const data = await response.json();

  if (!response.ok || !data.success) {
    return [];
  }

  return data.data;
}
